import { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BadgeCheck, Loader2, Phone, Mail, Building2, ImageUp, Trash2, Tag } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const EMPTY = { name: "", phone: "", email: "", logo_path: "", subject_template: "" };

export default function IssuerSettings() {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const applyData = (d) =>
    setForm({
      name: d.name || "",
      phone: d.phone || "",
      email: d.email || "",
      logo_path: d.logo_path || "",
      subject_template: d.subject_template || "",
    });

  useEffect(() => {
    api
      .get("/issuer-settings")
      .then((r) => applyData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("issuer.toast.nameRequired"));
    setSaving(true);
    try {
      const r = await api.put("/issuer-settings", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        subject_template: form.subject_template.trim(),
      });
      applyData(r.data);
      toast.success(t("issuer.toast.saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error(t("issuer.toast.imageOnly"));
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/issuer-settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyData(r.data);
      toast.success(t("issuer.toast.logoImported"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    try {
      const r = await api.delete("/issuer-settings/logo");
      applyData(r.data);
      toast.success(t("issuer.toast.logoRemoved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 lg:px-12" data-testid="issuer-settings-page">
      <header className="mb-8">
        <p className="overline text-[#C6A87C]">{t("issuer.overline")}</p>
        <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">{t("issuer.title")}</h1>
        <p className="mt-2 text-slate-500">
          {t("issuer.subtitle")}
        </p>
      </header>

      {loading ? (
        <div className="flex h-40 items-center justify-center text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
        >
          {/* Logo */}
          <div className="mb-6">
            <Label className="flex items-center gap-2 text-slate-700">
              <ImageUp className="h-4 w-4 text-[#C6A87C]" /> {t("issuer.logoLabel")}
            </Label>
            <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-center">
              <div
                className="flex h-24 w-48 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-dashed border-slate-300 bg-[#0F172A]"
                data-testid="issuer-logo-preview"
              >
                {form.logo_path ? (
                  <img src={fileUrl(form.logo_path)} alt={t("issuer.logoAlt")} className="max-h-20 max-w-[176px] object-contain" />
                ) : (
                  <span className="px-3 text-center text-xs text-slate-400">{t("issuer.noLogo")}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  data-testid="issuer-logo-input"
                  onChange={(e) => {
                    if (e.target.files?.[0]) uploadLogo(e.target.files[0]);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="outline"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid="issuer-logo-upload-btn"
                  className="rounded-full"
                >
                  {uploadingLogo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageUp className="mr-2 h-4 w-4" />}
                  {form.logo_path ? t("issuer.replaceLogo") : t("issuer.importLogo")}
                </Button>
                {form.logo_path && (
                  <Button
                    variant="outline"
                    onClick={removeLogo}
                    data-testid="issuer-logo-remove-btn"
                    className="rounded-full text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> {t("issuer.removeLogo")}
                  </Button>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-xs text-slate-400">{t("issuer.logoHint")}</p>
          </div>

          <div className="space-y-5 border-t border-slate-100 pt-6">
            <div>
              <Label className="flex items-center gap-2 text-slate-700">
                <Building2 className="h-4 w-4 text-[#C6A87C]" /> {t("issuer.nameLabel")}
              </Label>
              <Input
                data-testid="issuer-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Kazar Immobilier"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2 text-slate-700">
                <Phone className="h-4 w-4 text-[#C6A87C]" /> {t("issuer.phoneLabel")}
              </Label>
              <Input
                data-testid="issuer-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+33 6 95 82 48 02"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2 text-slate-700">
                <Mail className="h-4 w-4 text-[#C6A87C]" /> {t("issuer.emailLabel")}
              </Label>
              <Input
                data-testid="issuer-email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="kazar.hamid@live.fr"
                className="mt-1.5 h-11"
              />
              <p className="mt-1.5 text-xs text-slate-400">
                {t("issuer.emailHint")}
              </p>
            </div>
            <div>
              <Label className="flex items-center gap-2 text-slate-700">
                <Tag className="h-4 w-4 text-[#C6A87C]" /> {t("issuer.subjectLabel")}
              </Label>
              <Input
                data-testid="issuer-subject-template"
                value={form.subject_template}
                onChange={(e) => set("subject_template", e.target.value)}
                placeholder="Kazar Immobilier — Opportunité — {bien} — {ville}"
                className="mt-1.5 h-11"
              />
              <p className="mt-1.5 text-xs text-slate-400">
                {t("issuer.subjectHintPre")} <span className="font-medium text-slate-500">{"{bien}"}</span> {t("issuer.subjectHintPost")}{" "}
                <span className="font-medium text-slate-500">{"{ville}"}</span>{t("issuer.subjectHintEnd")}
              </p>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              data-testid="issuer-save-btn"
              className="rounded-full bg-[#0F172A] px-6 text-white hover:bg-[#1e293b]"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><BadgeCheck className="mr-2 h-4 w-4" /> {t("common.save")}</>)}
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
